import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import paymentService from '../services/paymentService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const serverUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.slice(0, -4) : apiBaseUrl;

const PaymentsPage = () => {
  // Read initial filter from URL hash (e.g., /payments#mess or /payments#hostel)
  const getInitialFilter = () => {
    const hash = window.location.hash.slice(1); // Remove the # prefix
    if (hash === 'mess') return { type: 'mess', status: '' };
    if (hash === 'hostel') return { type: 'hostel', status: '' };
    return { type: '', status: '' };
  };
  
  const [payments, setPayments] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState(getInitialFilter());
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [showPendingVerificationOnly, setShowPendingVerificationOnly] = useState(false);
  const [expandAwaitingVerification, setExpandAwaitingVerification] = useState(false);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementPeriod, setStatementPeriod] = useState('1m');
  const [stmtCustomStart, setStmtCustomStart] = useState('');
  const [stmtCustomEnd, setStmtCustomEnd] = useState('');

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    loadPendingVerifications();
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showDetailsModal) {
        setShowDetailsModal(false);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showDetailsModal]);

  useEffect(() => {
    // Search and date filtering functionality
    let filtered = payments;

    // Apply pending verification filter
    if (showPendingVerificationOnly) {
      const pendingVerificationIds = pendingVerifications.map(pv => pv.id);
      filtered = filtered.filter(p => pendingVerificationIds.includes(p.id));
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.id.toString().includes(search) ||
        `${p.student_name || ''}`.toLowerCase().includes(search) ||
        p.transaction_id?.toLowerCase().includes(search)
      );
    }

    // Apply date range filter
    if (startDate || endDate) {
      filtered = filtered.filter(p => {
        const paymentDate = new Date(p.created_at);
        if (startDate && new Date(startDate) > paymentDate) return false;
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999); // Include entire end date
          if (end < paymentDate) return false;
        }
        return true;
      });
    }

    setFilteredPayments(filtered);
  }, [searchTerm, payments, startDate, endDate, showPendingVerificationOnly, pendingVerifications]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      const response = await paymentService.getPaymentsAdmin(filter);
      setPayments(response.data?.data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError('Failed to load payments');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingVerifications = async () => {
    try {
      const response = await paymentService.getPendingVerifications();
      console.log('Pending verifications API response:', response);
      console.log('response.data:', response.data);
      
      // Handle both response.data.data and response.data (in case of different wrapper)
      const data = response.data?.data || response.data || [];
      console.log('Extracted data:', data);
      
      setPendingVerifications(data);
    } catch (err) {
      console.error('Error loading pending verifications:', err);
      console.error('Error details:', err.response?.data);
      setPendingVerifications([]);
    }
  };

  const handleVerifyPayment = async () => {
    if (!selectedVerification) return;
    
    setVerifying(true);
    try {
      // Use payment service for all payment types (mess and hostel)
      await paymentService.verifyPayment(selectedVerification.id, {
        notes: ''
      });
      
      // Reload both lists after verification
      await loadPendingVerifications();
      await loadPayments();
      
      setShowVerificationModal(false);
      setSelectedVerification(null);
      alert('Payment verified successfully!');
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      console.error('Error verifying payment:', err);
      alert('Failed to verify payment: ' + (err.response?.data?.message || err.message));
    } finally {
      setVerifying(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!selectedVerification) return;
    
    const rejectionReason = prompt('Enter rejection reason:', 'Payment verification failed');
    if (rejectionReason === null) return; // User cancelled
    
    setVerifying(true);
    try {
      await paymentService.rejectPayment(selectedVerification.id, {
        rejection_reason: rejectionReason
      });
      
      // Reload both lists after rejection
      await loadPendingVerifications();
      await loadPayments();
      
      setShowVerificationModal(false);
      setSelectedVerification(null);
      alert('Payment rejected successfully!');
      window.dispatchEvent(new Event('refreshPendingCounts'));
    } catch (err) {
      console.error('Error rejecting payment:', err);
      alert('Failed to reject payment: ' + (err.response?.data?.message || err.message));
    } finally {
      setVerifying(false);
    }
  };

  // Compute start/end Date objects for a statement period
  const getStatementRange = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    let start = new Date();
    start.setHours(0, 0, 0, 0);
    if (statementPeriod === '1m') { start.setMonth(start.getMonth() - 1); }
    else if (statementPeriod === '2m') { start.setMonth(start.getMonth() - 2); }
    else if (statementPeriod === '3m') { start.setMonth(start.getMonth() - 3); }
    else if (statementPeriod === '6m') { start.setMonth(start.getMonth() - 6); }
    else if (statementPeriod === '1y') { start.setFullYear(start.getFullYear() - 1); }
    else if (statementPeriod === 'custom') {
      start = stmtCustomStart ? new Date(stmtCustomStart) : new Date(0);
      start.setHours(0, 0, 0, 0);
      const end = stmtCustomEnd ? new Date(stmtCustomEnd) : today;
      end.setHours(23, 59, 59, 999);
      return { start, end };
    }
    return { start, end: today };
  };

  const getStatementPayments = () => {
    const { start, end } = getStatementRange();
    return payments.filter(p => {
      const d = new Date(p.created_at);
      return d >= start && d <= end && p.status === 'completed';
    });
  };

  const periodLabel = () => ({
    '1m': 'Last 1 Month', '2m': 'Last 2 Months', '3m': 'Last 3 Months',
    '6m': 'Last 6 Months', '1y': 'Last 1 Year', 'custom': 'Custom Range'
  })[statementPeriod] || '';

  const handleDownloadPDF = () => {
    const stmtPayments = getStatementPayments();
    const { start, end } = getStatementRange();
    const fmt = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const fmtCur = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);
    const messTotal = stmtPayments.filter(p => p.payment_type === 'mess').reduce((s, p) => s + (p.amount || 0), 0);
    const hostelRentTotal = stmtPayments.filter(p => p.payment_type === 'hostel_rent').reduce((s, p) => s + (p.amount || 0), 0);
    const depositTotal = stmtPayments.filter(p => p.payment_type === 'hostel_deposit').reduce((s, p) => s + (p.amount || 0), 0);
    const grandTotal = stmtPayments.reduce((s, p) => s + (p.amount || 0), 0);

    const doc = new jsPDF({ orientation: 'landscape' });

    // Header
    doc.setFillColor(132, 204, 22);
    doc.rect(0, 0, 297, 22, 'F');
    doc.setTextColor(54, 83, 20);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Sai Mess & Hostel', 14, 14);
    doc.setFontSize(10);
    doc.text('Account Statement', 200, 14);

    // Period info
    doc.setTextColor(55, 65, 81);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Period: ${periodLabel()}  |  ${fmt(start)} to ${fmt(end)}`, 14, 30);
    doc.text(`Generated: ${fmt(new Date())}`, 14, 36);

    // Summary row
    doc.setFontSize(9);
    const summaryY = 44;
    const summaryItems = [
      ['Total Collected', fmtCur(grandTotal)],
      ['Mess Payments', fmtCur(messTotal)],
      ['Hostel Rent', fmtCur(hostelRentTotal)],
      ['Security Deposits', fmtCur(depositTotal)],
      ['Transactions', String(stmtPayments.length)],
    ];
    summaryItems.forEach(([label, val], i) => {
      const x = 14 + i * 56;
      doc.setFillColor(240, 253, 244);
      doc.roundedRect(x, summaryY, 52, 18, 2, 2, 'F');
      doc.setTextColor(107, 114, 128);
      doc.setFont('helvetica', 'normal');
      doc.text(label, x + 3, summaryY + 6);
      doc.setTextColor(4, 120, 87);
      doc.setFont('helvetica', 'bold');
      doc.text(val, x + 3, summaryY + 13);
    });

    // Table
    const typeLabel = (t) => t === 'hostel_rent' ? 'Hostel Rent' : t === 'hostel_deposit' ? 'Security Deposit' : t || 'N/A';
    autoTable(doc, {
      startY: summaryY + 26,
      head: [['ID', 'Student', 'Type', 'Amount', 'Mode', 'Date', 'Transaction ID']],
      body: stmtPayments.map(p => [
        `#${p.id}`,
        p.student_name || 'N/A',
        typeLabel(p.payment_type),
        fmtCur(p.amount),
        p.payment_mode || 'N/A',
        new Date(p.created_at).toLocaleDateString('en-IN'),
        p.transaction_id || '-',
      ]),
      foot: [['', '', 'Grand Total', fmtCur(grandTotal), '', '', '']],
      headStyles: { fillColor: [132, 204, 22], textColor: [54, 83, 20], fontStyle: 'bold', fontSize: 9 },
      footStyles: { fillColor: [240, 253, 244], textColor: [4, 120, 87], fontStyle: 'bold', fontSize: 9 },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 3: { fontStyle: 'bold', textColor: [4, 120, 87] } },
    });

    doc.save(`Account_Statement_${periodLabel().replace(/ /g, '_')}_${fmt(new Date()).replace(/ /g, '-')}.pdf`);
  };

  const handleDownloadExcel = () => {
    const stmtPayments = getStatementPayments();
    const { start, end } = getStatementRange();
    const fmt = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const messTotal = stmtPayments.filter(p => p.payment_type === 'mess').reduce((s, p) => s + (p.amount || 0), 0);
    const hostelRentTotal = stmtPayments.filter(p => p.payment_type === 'hostel_rent').reduce((s, p) => s + (p.amount || 0), 0);
    const depositTotal = stmtPayments.filter(p => p.payment_type === 'hostel_deposit').reduce((s, p) => s + (p.amount || 0), 0);
    const grandTotal = stmtPayments.reduce((s, p) => s + (p.amount || 0), 0);
    const typeLabel = (t) => t === 'hostel_rent' ? 'Hostel Rent' : t === 'hostel_deposit' ? 'Security Deposit' : t || 'N/A';

    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ['Sai Mess & Hostel — Account Statement'],
      [`Period: ${periodLabel()}  |  ${fmt(start)} to ${fmt(end)}`],
      [`Generated: ${fmt(new Date())}`],
      [],
      ['Summary', ''],
      ['Total Collected', grandTotal],
      ['Mess Payments', messTotal],
      ['Hostel Rent', hostelRentTotal],
      ['Security Deposits', depositTotal],
      ['Total Transactions', stmtPayments.length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 28 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Transactions sheet
    const txnHeaders = [['ID', 'Student Name', 'Payment Type', 'Amount (INR)', 'Payment Mode', 'Date', 'Transaction ID', 'Status']];
    const txnRows = stmtPayments.map(p => [
      p.id,
      p.student_name || 'N/A',
      typeLabel(p.payment_type),
      p.amount || 0,
      p.payment_mode || 'N/A',
      new Date(p.created_at).toLocaleDateString('en-IN'),
      p.transaction_id || '-',
      p.status,
    ]);
    txnRows.push(['', '', 'Grand Total', grandTotal, '', '', '', '']);
    const txnSheet = XLSX.utils.aoa_to_sheet([...txnHeaders, ...txnRows]);
    txnSheet['!cols'] = [{ wch: 8 }, { wch: 24 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 28 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, txnSheet, 'Transactions');

    XLSX.writeFile(wb, `Account_Statement_${periodLabel().replace(/ /g, '_')}_${fmt(new Date()).replace(/ /g, '-')}.xlsx`);
  };

  const handlePrintStatement = () => {
    const stmtPayments = getStatementPayments();
    const { start, end } = getStatementRange();
    const fmt = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const fmtCur = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(n || 0);
    const messTotal = stmtPayments.filter(p => p.payment_type === 'mess').reduce((s, p) => s + (p.amount || 0), 0);
    const hostelRentTotal = stmtPayments.filter(p => p.payment_type === 'hostel_rent').reduce((s, p) => s + (p.amount || 0), 0);
    const depositTotal = stmtPayments.filter(p => p.payment_type === 'hostel_deposit').reduce((s, p) => s + (p.amount || 0), 0);
    const grandTotal = stmtPayments.reduce((s, p) => s + (p.amount || 0), 0);
    const rows = stmtPayments.map((p, i) => `
      <tr style="background:${i % 2 === 0 ? '#fff' : '#f9fafb'}">
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">#${p.id}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${p.student_name || 'N/A'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-transform:capitalize">${p.payment_type === 'hostel_rent' ? 'Hostel Rent' : p.payment_type === 'hostel_deposit' ? 'Security Deposit' : p.payment_type}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${fmtCur(p.amount)}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;text-transform:capitalize">${p.payment_mode || 'N/A'}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb">${new Date(p.created_at).toLocaleDateString('en-IN')}</td>
        <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;font-size:12px;color:#6b7280">${p.transaction_id || '-'}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Account Statement</title>
      <style>body{font-family:Arial,sans-serif;margin:0;padding:24px;color:#1f2937}
      h1{color:#365314;margin-bottom:4px}table{width:100%;border-collapse:collapse;margin-top:20px}
      th{background:#84cc16;color:#365314;padding:10px 12px;text-align:left;font-size:13px}
      .summary{display:flex;gap:16px;margin:16px 0}.summary-card{flex:1;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px}
      .summary-card h4{margin:0 0 4px;font-size:11px;color:#6b7280;text-transform:uppercase}
      .summary-card .val{font-size:18px;font-weight:700;color:#047857}
      @media print{.no-print{display:none}}</style></head><body>
      <h1>Sai Mess & Hostel</h1>
      <p style="color:#6b7280;margin:0">Account Statement &mdash; ${periodLabel()}</p>
      <p style="color:#374151;margin:8px 0"><strong>Period:</strong> ${fmt(start)} &rarr; ${fmt(end)}</p>
      <p style="color:#374151;margin:0"><strong>Generated:</strong> ${fmt(new Date())}</p>
      <div class="summary">
        <div class="summary-card"><h4>Total Collected</h4><div class="val">${fmtCur(grandTotal)}</div></div>
        <div class="summary-card"><h4>Mess Payments</h4><div class="val">${fmtCur(messTotal)}</div></div>
        <div class="summary-card"><h4>Hostel Rent</h4><div class="val">${fmtCur(hostelRentTotal)}</div></div>
        <div class="summary-card"><h4>Security Deposits</h4><div class="val">${fmtCur(depositTotal)}</div></div>
        <div class="summary-card"><h4>Transactions</h4><div class="val">${stmtPayments.length}</div></div>
      </div>
      <table><thead><tr><th>ID</th><th>Student</th><th>Type</th><th>Amount</th><th>Mode</th><th>Date</th><th>Transaction ID</th></tr></thead>
      <tbody>${rows}</tbody>
      <tfoot><tr style="background:#f0fdf4;font-weight:700"><td colspan="3" style="padding:10px 12px;text-align:right">Grand Total</td><td style="padding:10px 12px;color:#047857">${fmtCur(grandTotal)}</td><td colspan="3"></td></tr></tfoot>
      </table>
      <script>window.onload=()=>{window.print();}</script></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };

  const stats = {
    total: filteredPayments.filter(p => p.status !== 'rejected').length,
    totalAmount: filteredPayments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0),
    completed: filteredPayments.filter(p => p.status === 'completed').length,
    pending: filteredPayments.filter(p => p.status === 'pending').length,
    rejected: filteredPayments.filter(p => p.status === 'rejected').length,
    pendingVerifications: pendingVerifications.length,
  };

  return (
    <Layout title="Payments">
      <style>{`
        .filter-row { display: flex; gap: 12px; margin-bottom: 24px; align-items: center; flex-wrap: wrap; }
        .filter-select { padding: 10px 14px; border: 1px solid rgba(0, 0, 0, 0.04); border-radius: 12px; font-size: 14px; min-width: 160px; background: white; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .filter-select:focus { outline: none; border-color: #84cc16; box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.06); }
        .search-input { padding: 10px 14px; border: 1px solid rgba(0, 0, 0, 0.04); border-radius: 12px; font-size: 14px; min-width: 240px; background: white; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .search-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 6px 12px -2px rgba(59, 130, 246, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.06); }
        .search-button { padding: 10px 18px; background: #3b82f6; color: white; border: none; border-radius: 12px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 8px -2px rgba(59, 130, 246, 0.1); }
        .search-button:hover { background: #2563eb; box-shadow: 0 6px 12px -2px rgba(59, 130, 246, 0.2); }
        .search-button:active { transform: scale(0.98); }
        .reset-button { padding: 10px 18px; background: #84cc16; color: #365314; border: none; border-radius: 12px; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 8px -2px rgba(132, 204, 22, 0.1); }
        .reset-button:hover { background: #65a30d; color: white; box-shadow: 0 6px 12px -2px rgba(132, 204, 22, 0.2); }
        .reset-button:active { transform: scale(0.98); }
        .date-input { padding: 10px 14px; border: 1px solid rgba(0, 0, 0, 0.04); border-radius: 12px; font-size: 14px; background: white; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .date-input:focus { outline: none; border-color: #f59e0b; box-shadow: 0 6px 12px -2px rgba(245, 158, 11, 0.2), 0 10px 20px -5px rgba(0, 0, 0, 0.06); }
        .date-filter-label { font-size: 12px; color: #6b7280; font-weight: 500; text-transform: uppercase; margin-right: 6px; }
        .stats-row { display: flex; gap: 20px; margin-bottom: 24px; }
        .stat-card { background: white; padding: 20px; border-radius: 16px; border: 1px solid rgba(0, 0, 0, 0.04); flex: 1; box-shadow: 0 6px 12px -2px rgba(16, 185, 129, 0.15), 0 10px 20px -5px rgba(0, 0, 0, 0.05); transition: all 0.2s ease; }
        .stat-card:hover { transform: translateY(-2px); box-shadow: 0 10px 20px -4px rgba(16, 185, 129, 0.2), 0 15px 25px -8px rgba(0, 0, 0, 0.08); }
        .stat-card h3 { font-size: 12px; color: #6b7280; margin: 0 0 8px; text-transform: uppercase; }
        .stat-card .value { font-size: 28px; font-weight: 600; color: #1f2937; }
        .stat-card .value.green { color: #047857; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        .data-table td { padding: 14px 16px; border-bottom: 1px solid #f3f4f6; color: #374151; font-size: 14px; }
        .data-table tr:hover { background: #f9fafb; }
        .status-badge { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
        .status-completed { background: #d1fae5; color: #047857; }
        .status-pending { background: #fef3c7; color: #b45309; }
        .status-rejected { background: #fee2e2; color: #dc2626; }
        .status-failed { background: #fee2e2; color: #dc2626; }
        .type-badge { padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 500; }
        .type-mess { background: #fef3c7; color: #b45309; }
        .type-hostel { background: #e0e7ff; color: #4338ca; }
        .amount { font-weight: 600; color: #047857; }
        .loading, .error, .empty { text-align: center; padding: 48px; color: #6b7280; }
        .error { color: #dc2626; }
        .table-wrapper { overflow-x: scroll; overflow-y: auto; width: 100%; border: 1px solid #e5e7eb; border-radius: 8px; background: white; max-height: 70vh; }
        .table-wrapper::-webkit-scrollbar { height: 12px; }
        .table-wrapper::-webkit-scrollbar-track { background: #f3f4f6; border-radius: 6px; }
        .table-wrapper::-webkit-scrollbar-thumb { background: #9ca3af; border-radius: 6px; }
        .table-wrapper::-webkit-scrollbar-thumb:hover { background: #6b7280; }
        /* Firefox scrollbar */
        .table-wrapper { scrollbar-color: #9ca3af #f3f4f6; scrollbar-width: thin; }
        .stmt-period-btn { padding: 8px 16px; border-radius: 20px; border: 2px solid #e5e7eb; background: white; cursor: pointer; font-size: 13px; font-weight: 600; color: #374151; transition: all 0.18s; }
        .stmt-period-btn:hover { border-color: #84cc16; color: #365314; background: #f7fee7; }
        .stmt-period-btn.active { border-color: #84cc16; background: #84cc16; color: #365314; }
        .stmt-summary-card { flex: 1; background: white; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px 20px; min-width: 120px; }
        .stmt-summary-card h4 { margin: 0 0 6px; font-size: 11px; color: #6b7280; text-transform: uppercase; font-weight: 600; }
        .stmt-summary-card .val { font-size: 20px; font-weight: 700; color: #047857; }
        .stmt-summary-card .val.blue { color: #1d4ed8; }
        .stmt-summary-card .val.orange { color: #b45309; }
        .stmt-summary-card .val.purple { color: #7c3aed; }
        .stmt-summary-card .val.count { color: #374151; }
      `}</style>

      {/* Account Statement Bar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
        <button
          onClick={() => setShowStatementModal(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 20px', background: 'linear-gradient(135deg, #84cc16, #65a30d)',
            color: '#fff', border: 'none', borderRadius: '12px',
            fontWeight: '700', fontSize: '14px', cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(132,204,22,0.35)'
          }}
        >
          📊 Account Statement
        </button>
      </div>

      <div className="filter-row">
        <input 
          type="text" 
          className="search-input" 
          placeholder="Search by ID, Student Name, Transaction ID..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button 
          className="reset-button"
          onClick={() => setSearchTerm('')}
        >
          Clear
        </button>
        <select className="filter-select" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}>
          <option value="">All Types</option>
          <option value="mess">Mess</option>
          <option value="hostel">Hostel (All)</option>
          <option value="hostel_rent">Hostel Rent</option>
          <option value="hostel_deposit">Security Deposit</option>
        </select>
        <select className="filter-select" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="rejected">Rejected</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      <div className="filter-row">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label className="date-filter-label">From:</label>
          <input 
            type="date" 
            className="date-input" 
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label className="date-filter-label">To:</label>
          <input 
            type="date" 
            className="date-input" 
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <button 
          className="reset-button"
          onClick={() => { setStartDate(''); setEndDate(''); }}
          style={{ padding: '8px 12px', fontSize: '12px' }}
        >
          Reset Dates
        </button>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <h3>Total Payments</h3>
          <div className="value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <h3>Total Amount</h3>
          <div className="value green">{formatCurrency(stats.totalAmount)}</div>
        </div>
        <div className="stat-card">
          <h3>Completed</h3>
          <div className="value">{stats.completed}</div>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <div className="value">{stats.pending}</div>
        </div>
        <div className="stat-card">
          <h3>Rejected</h3>
          <div className="value" style={{ color: '#dc2626' }}>{stats.rejected}</div>
        </div>
        <div 
          className="stat-card" 
          onClick={() => setExpandAwaitingVerification(!expandAwaitingVerification)}
          style={{ 
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: expandAwaitingVerification ? '#f0fdf4' : 'white',
            border: expandAwaitingVerification ? '2px solid #10B981' : '1px solid rgba(0, 0, 0, 0.04)',
          }}
          onMouseEnter={(e) => {
            if (!expandAwaitingVerification) {
              e.currentTarget.style.backgroundColor = '#f0fdf4';
              e.currentTarget.style.border = '1px solid #10B981';
            }
          }}
          onMouseLeave={(e) => {
            if (!expandAwaitingVerification) {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.border = '1px solid rgba(0, 0, 0, 0.04)';
            }
          }}
        >
          <h3 style={{ color: '#10B981', margin: '0 0 8px', fontSize: '12px', textTransform: 'uppercase' }}>Awaiting Verification</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="value" style={{ color: '#10B981', margin: 0, fontSize: '28px', fontWeight: '600' }}>{stats.pendingVerifications}</div>
            <span style={{ fontSize: '20px', transition: 'transform 0.2s' }}>{expandAwaitingVerification ? '▼' : '▶'}</span>
          </div>
        </div>
      </div>

      {expandAwaitingVerification && pendingVerifications.length > 0 && (
        <div style={{
          background: 'white',
          border: '2px solid #10B981',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: '600', color: '#047857' }}>
            Pending Verification Payments ({pendingVerifications.length})
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {pendingVerifications.map((verification) => (
              <div key={verification.admission_id} style={{
                background: '#f0fdf4',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #d1fae5',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.1)'
              }}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '15px', color: '#1f2937' }}>{verification.student_name}</strong>
                  <p style={{ margin: '4px 0', fontSize: '13px', color: '#6b7280' }}>{verification.student_email}</p>
                </div>
                <div style={{ marginBottom: '12px', paddingTop: '12px', borderTop: '1px solid #d1fae5' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                    <div>
                      <span style={{ color: '#6b7280' }}>Amount:</span>
                      <strong style={{ display: 'block', color: '#047857', fontSize: '15px' }}>{formatCurrency(verification.amount)}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Type:</span>
                      <strong style={{ display: 'block', color: '#1f2937', textTransform: 'capitalize' }}>
                        {verification.payment_type}
                      </strong>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Hostel:</span>
                      <strong style={{ display: 'block', color: '#1f2937' }}>{verification.hostel_name || '-'}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#6b7280' }}>Room:</span>
                      <strong style={{ display: 'block', color: '#1f2937' }}>{verification.room_number || '-'}</strong>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setSelectedVerification(verification);
                    setShowVerificationModal(true);
                  }}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#059669'}
                  onMouseLeave={(e) => e.target.style.background = '#10B981'}
                >
                  Review & Verify
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showPendingVerificationOnly && (
        <div style={{
          background: '#f0fdf4',
          border: '2px solid #10B981',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ color: '#065f46', fontWeight: '500', fontSize: '14px' }}>
            🔍 Showing only pending verification payments
          </span>
          <button
            onClick={() => setShowPendingVerificationOnly(false)}
            style={{
              background: '#10B981',
              color: 'white',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '12px'
            }}
          >
            Clear Filter
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading payments...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : payments.length === 0 ? (
        <div className="empty">No payments found</div>
      ) : filteredPayments.length === 0 ? (
        <div className="empty">No payments match your search</div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table" style={{ minWidth: '1400px', width: '100%', margin: 0 }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Student</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Mode of Transaction</th>
                <th>Date</th>
                <th>Status</th>
                <th>Transaction ID</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr key={payment.id}>
                  <td>#{payment.id}</td>
                  <td><strong>{payment.student_name || 'N/A'}</strong></td>
                  <td><span className={`type-badge ${payment.payment_type?.startsWith('hostel') ? 'type-hostel' : `type-${payment.payment_type}`}`}>
                    {payment.payment_type === 'hostel_rent' ? 'Hostel Rent' :
                     payment.payment_type === 'hostel_deposit' ? 'Security Deposit' :
                     payment.payment_type}
                  </span></td>
                  <td className="amount">{formatCurrency(payment.amount)}</td>
                  <td>
                    <span className={`mode-badge mode-${payment.payment_mode}`} style={{
                      padding: '4px 10px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: payment.payment_mode === 'cash' ? '#dcfce7' : 
                                     payment.payment_mode === 'upi' ? '#dbeafe' : 
                                     payment.payment_mode === 'bank_transfer' ? '#fef3c7' : '#f3f4f6',
                      color: payment.payment_mode === 'cash' ? '#166534' : 
                             payment.payment_mode === 'upi' ? '#1e40af' : 
                             payment.payment_mode === 'bank_transfer' ? '#854d0e' : '#6b7280'
                    }}>
                      {payment.payment_mode === 'cash' ? 'Cash' : 
                       payment.payment_mode === 'upi' ? 'UPI' : 
                       payment.payment_mode === 'bank_transfer' ? 'Internet Banking' : 
                       payment.payment_mode || 'N/A'}
                    </span>
                  </td>
                  <td>{formatDate(payment.created_at)}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className={`status-badge status-${payment.status}`}>{payment.status}</span>
                      {(() => {
                        const desc = payment.description || '';
                        if (!desc.includes(' | ')) return null;
                        const period = desc.split(' | ').pop().trim();
                        if (!period.includes(' to ')) return null;
                        const endStr = period.split(' to ').pop().trim();
                        const parts = endStr.split('/');
                        if (parts.length !== 3) return null;
                        const endDate = new Date(`${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`);
                        const startOfToday = new Date();
                        startOfToday.setHours(0, 0, 0, 0);
                        if (isNaN(endDate) || endDate >= startOfToday) return null;
                        return (
                          <span style={{
                            background: '#fef2f2',
                            color: '#dc2626',
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            border: '1px solid #fecaca',
                            display: 'inline-block'
                          }}>Subscription Ended</span>
                        );
                      })()}
                    </div>
                  </td>
                  <td style={{ color: '#6b7280', fontSize: '13px' }}>{payment.transaction_id || 'N/A'}</td>
                  <td>
                    <button 
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                      onClick={() => {
                        setSelectedPayment(payment);
                        setShowDetailsModal(true);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Account Statement Modal */}
      {showStatementModal && (() => {
        const stmtPayments = getStatementPayments();
        const { start, end } = getStatementRange();
        const fmtD = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
        const messTotal = stmtPayments.filter(p => p.payment_type === 'mess').reduce((s, p) => s + (p.amount || 0), 0);
        const hostelRentTotal = stmtPayments.filter(p => p.payment_type === 'hostel_rent').reduce((s, p) => s + (p.amount || 0), 0);
        const depositTotal = stmtPayments.filter(p => p.payment_type === 'hostel_deposit').reduce((s, p) => s + (p.amount || 0), 0);
        const grandTotal = stmtPayments.reduce((s, p) => s + (p.amount || 0), 0);
        return (
          <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
          }} onClick={() => setShowStatementModal(false)}>
            <div style={{
              background: 'white', borderRadius: '16px', padding: '28px',
              width: '92%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }} onClick={e => e.stopPropagation()}>

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '700', color: '#1f2937' }}>📊 Account Statement</h2>
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>Completed payments only</p>
                </div>
                <button onClick={() => setShowStatementModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>

              {/* Period Selector */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151', marginRight: '4px' }}>Period:</span>
                {[['1m','1 Month'],['2m','2 Months'],['3m','3 Months'],['6m','6 Months'],['1y','1 Year'],['custom','Custom']].map(([val, label]) => (
                  <button key={val} className={`stmt-period-btn${statementPeriod === val ? ' active' : ''}`}
                    onClick={() => setStatementPeriod(val)}>{label}</button>
                ))}
              </div>

              {/* Custom Date Range */}
              {statementPeriod === 'custom' && (
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>FROM:</label>
                    <input type="date" className="date-input" value={stmtCustomStart}
                      onChange={e => setStmtCustomStart(e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <label style={{ fontSize: '12px', color: '#6b7280', fontWeight: '600' }}>TO:</label>
                    <input type="date" className="date-input" value={stmtCustomEnd}
                      onChange={e => setStmtCustomEnd(e.target.value)} />
                  </div>
                </div>
              )}

              {/* Period Info */}
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 16px', marginBottom: '20px', fontSize: '13px', color: '#065f46' }}>
                <strong>{periodLabel()}</strong> &nbsp;|&nbsp; {fmtD(start)} → {fmtD(end)}
              </div>

              {/* Summary Cards */}
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <div className="stmt-summary-card" style={{ borderTop: '4px solid #84cc16' }}>
                  <h4>Total Collected</h4>
                  <div className="val">{formatCurrency(grandTotal)}</div>
                </div>
                <div className="stmt-summary-card" style={{ borderTop: '4px solid #f59e0b' }}>
                  <h4>Mess Payments</h4>
                  <div className="val orange">{formatCurrency(messTotal)}</div>
                </div>
                <div className="stmt-summary-card" style={{ borderTop: '4px solid #6366f1' }}>
                  <h4>Hostel Rent</h4>
                  <div className="val purple">{formatCurrency(hostelRentTotal)}</div>
                </div>
                <div className="stmt-summary-card" style={{ borderTop: '4px solid #3b82f6' }}>
                  <h4>Security Deposits</h4>
                  <div className="val blue">{formatCurrency(depositTotal)}</div>
                </div>
                <div className="stmt-summary-card" style={{ borderTop: '4px solid #e5e7eb' }}>
                  <h4>Transactions</h4>
                  <div className="val count">{stmtPayments.length}</div>
                </div>
              </div>

              {/* Transactions Table */}
              {stmtPayments.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px', color: '#9ca3af', fontSize: '15px' }}>No completed payments in this period</div>
              ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'auto', maxHeight: '320px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        {['ID','Student','Type','Amount','Mode','Date','Transaction ID'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', textAlign: 'left', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stmtPayments.map((p, i) => (
                        <tr key={p.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '9px 14px', fontSize: '13px', color: '#374151', borderBottom: '1px solid #f3f4f6' }}>#{p.id}</td>
                          <td style={{ padding: '9px 14px', fontSize: '13px', fontWeight: '600', borderBottom: '1px solid #f3f4f6' }}>{p.student_name || 'N/A'}</td>
                          <td style={{ padding: '9px 14px', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}>
                            <span className={`type-badge ${p.payment_type?.startsWith('hostel') ? 'type-hostel' : `type-${p.payment_type}`}`}>
                              {p.payment_type === 'hostel_rent' ? 'Hostel Rent' : p.payment_type === 'hostel_deposit' ? 'Security Deposit' : p.payment_type}
                            </span>
                          </td>
                          <td style={{ padding: '9px 14px', fontSize: '13px', fontWeight: '700', color: '#047857', borderBottom: '1px solid #f3f4f6' }}>{formatCurrency(p.amount)}</td>
                          <td style={{ padding: '9px 14px', fontSize: '12px', textTransform: 'capitalize', borderBottom: '1px solid #f3f4f6' }}>{p.payment_mode || 'N/A'}</td>
                          <td style={{ padding: '9px 14px', fontSize: '13px', borderBottom: '1px solid #f3f4f6' }}>{formatDate(p.created_at)}</td>
                          <td style={{ padding: '9px 14px', fontSize: '12px', color: '#6b7280', borderBottom: '1px solid #f3f4f6' }}>{p.transaction_id || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f0fdf4' }}>
                        <td colSpan={3} style={{ padding: '10px 14px', fontWeight: '700', fontSize: '13px', textAlign: 'right', color: '#374151' }}>Grand Total</td>
                        <td style={{ padding: '10px 14px', fontWeight: '800', fontSize: '14px', color: '#047857' }}>{formatCurrency(grandTotal)}</td>
                        <td colSpan={3}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
                <button onClick={handleDownloadPDF} style={{
                  flex: 1, padding: '11px', background: '#dc2626', color: 'white',
                  border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}>📄 Download PDF</button>
                <button onClick={handleDownloadExcel} style={{
                  flex: 1, padding: '11px', background: '#16a34a', color: 'white',
                  border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}>📊 Download Excel</button>
                <button onClick={handlePrintStatement} style={{
                  flex: 1, padding: '11px', background: '#1f2937', color: 'white',
                  border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '14px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}>🖨️ Print</button>
                <button onClick={() => setShowStatementModal(false)} style={{
                  flex: 1, padding: '11px', background: '#84cc16', color: '#365314',
                  border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '14px'
                }}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment Details Modal */}
      {showDetailsModal && selectedPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => setShowDetailsModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '28px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '600', color: '#1f2937' }}>
                Payment Details
              </h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: '#6b7280'
                }}>
                ×
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Payment ID
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  #{selectedPayment.id}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Transaction ID
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedPayment.transaction_id || 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Student Name
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedPayment.student_name || 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Email
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedPayment.student_email || 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Payment Type
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500', textTransform: 'capitalize' }}>
                  {selectedPayment.payment_type === 'hostel_rent' ? 'Hostel Rent' :
                   selectedPayment.payment_type === 'hostel_deposit' ? 'Security Deposit' :
                   selectedPayment.payment_type}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Amount
                </label>
                <p style={{ margin: 0, fontSize: '16px', color: '#047857', fontWeight: '700' }}>
                  {formatCurrency(selectedPayment.amount)}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Payment Mode
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500', textTransform: 'capitalize' }}>
                  {selectedPayment.payment_mode === 'cash' ? 'Cash' :
                   selectedPayment.payment_mode === 'upi' ? 'UPI' :
                   selectedPayment.payment_mode === 'bank_transfer' ? 'Internet Banking' :
                   selectedPayment.payment_mode || 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Status
                </label>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: '600',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  backgroundColor: selectedPayment.status === 'completed' ? '#dcfce7' :
                                   selectedPayment.status === 'pending' ? '#fef3c7' : '#fee2e2',
                  color: selectedPayment.status === 'completed' ? '#166534' :
                         selectedPayment.status === 'pending' ? '#92400e' : '#991b1b',
                  textTransform: 'uppercase',
                  width: 'fit-content'
                }}>
                  {selectedPayment.status}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Date
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {formatDate(selectedPayment.created_at)}
                </p>
              </div>
            </div>

            {selectedPayment.notes && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Notes
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', padding: '8px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                  {selectedPayment.notes}
                </p>
              </div>
            )}

            {selectedPayment.payment_proof_url && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                  Payment Screenshot
                </label>
                <div style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#f3f4f6',
                  maxHeight: '300px'
                }}>
                  <img 
                    src={`${serverUrl}${selectedPayment.payment_proof_url}`}
                    alt="Payment Screenshot"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      maxHeight: '300px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <a 
                  href={`${serverUrl}${selectedPayment.payment_proof_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#3b82f6',
                    textDecoration: 'none',
                    cursor: 'pointer'
                  }}
                >
                  View Full Size →
                </a>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <button
                onClick={() => {
                  const token = localStorage.getItem('access_token');
                  const apiUrl = serverUrl;
                  fetch(`${apiUrl}/api/payments/receipt/${selectedPayment.id}/download`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  })
                  .then(r => {
                    if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); });
                    return r.blob();
                  })
                  .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
                  })
                  .catch(err => alert('Error viewing receipt: ' + err.message));
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                View Receipt
              </button>
              <button
                onClick={() => {
                  const token = localStorage.getItem('access_token');
                  const apiUrl = serverUrl;
                  const link = document.createElement('a');
                  link.href = `${apiUrl}/api/payments/receipt/${selectedPayment.id}/download`;
                  link.setAttribute('download', `Receipt-${selectedPayment.id}.pdf`);
                  fetch(link.href, {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  })
                  .then(r => r.blob())
                  .then(blob => {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `Receipt-${selectedPayment.id}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                  })
                  .catch(err => console.error('Error downloading receipt:', err));
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                Download Receipt
              </button>
            </div>

            {selectedPayment.payment_proof_url && selectedPayment.status === 'pending' && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <button
                  onClick={() => {
                    setSelectedVerification(selectedPayment);
                    setShowDetailsModal(false);
                    setShowVerificationModal(true);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                  ✓ Verify Payment
                </button>
                <button
                  onClick={() => {
                    const rejectionReason = prompt('Enter rejection reason:', 'Screenshot verification failed');
                    if (rejectionReason === null) return;
                    
                    const token = localStorage.getItem('access_token');
                    const apiUrl = serverUrl;
                    fetch(`${apiUrl}/api/payments/${selectedPayment.id}/reject`, {
                      method: 'POST',
                      headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({ rejection_reason: rejectionReason })
                    })
                    .then(r => {
                      if (!r.ok) return r.json().then(e => { throw new Error(e.message || 'Error'); });
                      return r.json();
                    })
                    .then(() => {
                      alert('Payment rejected successfully!');
                      setShowDetailsModal(false);
                      loadPayments();
                      loadPendingVerifications();
                    })
                    .catch(err => alert('Error rejecting payment: ' + err.message));
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}>
                  ✗ Reject Payment
                </button>
              </div>
            )}

            <button
              onClick={() => setShowDetailsModal(false)}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#84cc16',
                color: '#365314',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px'
              }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Payment Verification Modal */}
      {showVerificationModal && selectedVerification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}
        onClick={() => !verifying && setShowVerificationModal(false)}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '28px',
            maxWidth: '700px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
          }}
          onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '22px', fontWeight: '600', color: '#1f2937' }}>
                Payment Verification
              </h2>
              <button
                onClick={() => !verifying && setShowVerificationModal(false)}
                disabled={verifying}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: verifying ? 'not-allowed' : 'pointer',
                  color: '#6b7280',
                  opacity: verifying ? 0.5 : 1
                }}>
                ×
              </button>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Student Name
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedVerification.student_name}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Email
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedVerification.student_email}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  College ID
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedVerification.college_id || 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Amount
                </label>
                <p style={{ margin: 0, fontSize: '16px', color: '#047857', fontWeight: '700' }}>
                  {formatCurrency(selectedVerification.amount)}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Payment Method
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500', textTransform: 'capitalize' }}>
                  {selectedVerification.payment_method === 'upi' ? 'UPI' : 
                   selectedVerification.payment_method === 'bank_transfer' ? 'Bank Transfer' :
                   selectedVerification.payment_method}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Payment Date
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedVerification.payment_date ? new Date(selectedVerification.payment_date).toLocaleString() : 'N/A'}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Hostel
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedVerification.hostel_name}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Room Number
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedVerification.room_number}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Plan Type
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500', textTransform: 'capitalize' }}>
                  {selectedVerification.plan_type}
                </p>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '4px' }}>
                  Admission Date
                </label>
                <p style={{ margin: 0, fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                  {selectedVerification.admission_date ? formatDate(selectedVerification.admission_date) : 'N/A'}
                </p>
              </div>
            </div>

            {/* Payment Screenshot */}
            {(selectedVerification.payment_screenshot_url || selectedVerification.payment_proof_url) && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
                  Payment Screenshot
                </label>
                <div style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '8px',
                  backgroundColor: '#f9fafb',
                  textAlign: 'center'
                }}>
                  <img 
                    src={`${serverUrl}${selectedVerification.payment_screenshot_url || selectedVerification.payment_proof_url}`}
                    alt="Payment Screenshot"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '400px',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.open(`${serverUrl}${selectedVerification.payment_screenshot_url || selectedVerification.payment_proof_url}`, '_blank');
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px', fontStyle: 'italic' }}>
                    Click image to view full size
                  </p>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleVerifyPayment}
                disabled={verifying}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: verifying ? '#9ca3af' : '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: verifying ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: verifying ? 0.6 : 1
                }}>
                {verifying ? 'Processing...' : '✓ Verify Payment'}
              </button>
              <button
                onClick={handleRejectPayment}
                disabled={verifying}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: verifying ? '#9ca3af' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: verifying ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: verifying ? 0.6 : 1
                }}>
                {verifying ? 'Processing...' : '✗ Reject Payment'}
              </button>
            </div>

            <button
              onClick={() => !verifying && setShowVerificationModal(false)}
              disabled={verifying}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                backgroundColor: '#84cc16',
                color: '#365314',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: verifying ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: verifying ? 0.6 : 1
              }}>
              Close
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PaymentsPage;
